'use client';

import Link from 'next/link';
import {
  BarChart3,
  Bell,
  Building2,
  Calendar,
  CalendarDays,
  ChevronRight,
  FileText,
  Loader2,
  Plus,
  Settings,
  Sparkles,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react';
import { UserStatCard } from '@/components/user-dashboard/UserStatCard';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { resolveStorageUrl } from '@/lib/storage-url';

export type OrganizationDashboardData = {
  organization: { id: string; name: string; type?: string; domain?: string; logo_url?: string | null };
  total_events: number;
  active_events?: number;
  total_organizers: number;
  total_participants: number;
  total_registrations?: number;
  total_revenue: number;
  pending_approvals?: number;
};

export type RecentEvent = {
  id: string;
  title: string;
  status?: string;
  lifecycle_status?: string;
  start_date?: string;
  registrations_count?: number;
};

type Props = {
  userName?: string | null;
  loading: boolean;
  eventsLoading: boolean;
  data: OrganizationDashboardData | null;
  recentEvents: RecentEvent[];
  createEventHref: string;
  error?: string | null;
};

const EVENT_STATUS_CLASS: Record<string, string> = {
  published: 'org-status-badge--published',
  draft: 'org-status-badge--draft',
  live: 'org-status-badge--live',
  cancelled: 'org-status-badge--cancelled',
  archived: 'org-status-badge--archived',
};

function greetingForHour(hour: number) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatEventDate(value?: string) {
  if (!value) return 'Date TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function eventStatusLabel(event: RecentEvent) {
  return (event.lifecycle_status || event.status || 'draft').replace(/_/g, ' ');
}

function progressPercent(value: number, target: number) {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((value / target) * 100));
}

export function OrganizationHomeDashboard({
  userName,
  loading,
  eventsLoading,
  data,
  recentEvents,
  createEventHref,
  error,
}: Props) {
  if (loading) {
    return (
      <div className="org-page">
        <div className="org-skeleton-hero ud-enter" />
        <div className="org-stat-grid ud-enter ud-enter-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="org-skeleton-tile" />
          ))}
        </div>
        <div className="org-main-grid ud-enter ud-enter-3">
          <div className="org-left-stack">
            <div className="org-skeleton-panel" style={{ minHeight: '12rem' }} />
            <div className="org-skeleton-panel" style={{ minHeight: '10rem' }} />
            <div className="org-skeleton-panel" style={{ minHeight: '10rem' }} />
          </div>
          <div className="org-skeleton-panel" style={{ minHeight: '18rem' }} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="org-page">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
          {error || 'Organization data not available.'}
        </div>
      </div>
    );
  }

  const registrations = data.total_registrations ?? data.total_participants;
  const pendingApprovals = data.pending_approvals ?? 0;
  const isNewOrg = data.total_events <= 1 && data.total_organizers === 0 && registrations === 0;
  const firstName = userName?.split(' ')[0];
  const greeting = greetingForHour(new Date().getHours());
  const logoUrl = resolveStorageUrl(data.organization.logo_url);
  const orgType = data.organization.type?.replace(/_/g, ' ');

  const stats = [
    {
      label: 'Total events',
      value: data.total_events,
      icon: Calendar,
      href: '/dashboard/organization/events',
      hint: 'All events',
      accent: 'green' as const,
    },
    {
      label: 'Active events',
      value: data.active_events ?? 0,
      icon: Sparkles,
      href: '/dashboard/organization/events',
      hint: 'Live or upcoming',
      accent: 'mist' as const,
    },
    {
      label: 'Organizers',
      value: data.total_organizers,
      icon: UserCheck,
      href: '/dashboard/organization/organizers',
      hint: 'Active team',
      accent: 'green' as const,
    },
    {
      label: 'Registrations',
      value: registrations,
      icon: Users,
      href: '/dashboard/organization/analytics',
      hint: 'Confirmed sign-ups',
      accent: 'mist' as const,
    },
    {
      label: 'Organizers',
      value: data.total_organizers,
      icon: UserCheck,
      href: '/dashboard/organization/organizers',
      hint: 'Team members',
      accent: 'amber' as const,
    },
    {
      label: 'Pending approvals',
      value: pendingApprovals,
      icon: Bell,
      href: '/dashboard/organization/organizer-requests',
      hint: pendingApprovals > 0 ? 'Needs review' : 'All caught up',
      accent: pendingApprovals > 0 ? ('amber' as const) : ('green' as const),
    },
  ];

  const actions = [
    {
      label: 'Create event',
      description: 'Launch a new event for your organization',
      href: createEventHref,
      icon: Plus,
      featured: true,
    },
    {
      label: 'Organizers',
      description: 'Manage your team',
      href: '/dashboard/organization/organizers',
      icon: UserCheck,
    },
    {
      label: 'Analytics',
      description: 'Performance reports',
      href: '/dashboard/organization/analytics',
      icon: BarChart3,
    },
    {
      label: 'Profile',
      description: 'Branding & details',
      href: '/dashboard/organization/profile',
      icon: Building2,
    },
    {
      label: 'Settings',
      description: 'Domain & prefs',
      href: '/dashboard/organization/settings',
      icon: Settings,
    },
  ];

  const startSteps = [
    { label: 'Complete organization profile', href: '/dashboard/organization/profile' },
    { label: 'Invite your first organizer', href: '/dashboard/organization/organizers' },
    { label: 'Create and publish an event', href: createEventHref },
  ];

  const gridFillers = [
    data.total_organizers === 0
      ? {
          label: 'Invite organizers',
          description: 'Build your team to manage events',
          href: '/dashboard/organization/organizers',
          icon: UserCheck,
        }
      : null,
    {
      label: 'Complete profile',
      description: 'Add logo, branding & public details',
      href: '/dashboard/organization/profile',
      icon: Building2,
    },
    registrations === 0
      ? {
          label: 'Share your event',
          description: 'Publish and promote to get sign-ups',
          href: recentEvents[0] ? `/dashboard/events/${recentEvents[0].id}` : createEventHref,
          icon: TrendingUp,
        }
      : null,
    {
      label: 'Set up forms',
      description: 'Create registration & feedback forms',
      href: '/dashboard/forms',
      icon: FileText,
    },
    {
      label: 'Create another event',
      description: 'Grow your organization portfolio',
      href: createEventHref,
      icon: Plus,
    },
  ].filter(Boolean) as { label: string; description: string; href: string; icon: typeof Plus }[];

  const emptyGridSlots = Math.max(0, 4 - recentEvents.length);
  const visibleFillers = gridFillers.slice(0, emptyGridSlots);

  const setupMetrics = [
    {
      label: 'Events created',
      value: data.total_events,
      target: 3,
      href: '/dashboard/organization/events',
    },
    {
      label: 'Team members',
      value: data.total_organizers,
      target: 3,
      href: '/dashboard/organization/organizers',
    },
    {
      label: 'Registrations',
      value: registrations,
      target: 10,
      href: '/dashboard/organization/analytics',
    },
    {
      label: 'Active events',
      value: data.active_events ?? 0,
      target: 5,
      href: '/dashboard/organization/events',
    },
  ];

  const growthTips = [
    {
      title: 'Invite your first organizer',
      description: 'Delegate event management and scale faster.',
      href: '/dashboard/organization/organizers',
      icon: UserCheck,
      show: data.total_organizers === 0,
    },
    {
      title: 'Complete organization profile',
      description: 'Add your logo and public details for trust.',
      href: '/dashboard/organization/profile',
      icon: Building2,
      show: true,
    },
    {
      title: 'Configure event forms',
      description: 'Collect registrations with custom forms.',
      href: '/dashboard/forms',
      icon: FileText,
      show: true,
    },
    {
      title: 'Review analytics',
      description: 'Track sign-ups and event performance.',
      href: '/dashboard/organization/analytics',
      icon: BarChart3,
      show: registrations === 0,
    },
  ].filter((tip) => tip.show);

  return (
    <div className="org-page pb-10">
      <div className="ud-enter">
        <OnboardingWizard />
      </div>

      {pendingApprovals > 0 ? (
        <div className="org-alert ud-enter ud-enter-2">
          <div className="org-alert-body">
            <div className="org-alert-icon">
              <Bell className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <p className="text-sm font-semibold text-[var(--color-text)]">
                {pendingApprovals} organizer request{pendingApprovals === 1 ? '' : 's'} waiting
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-[var(--color-text-muted)]">
                Review and approve access before organizers can manage events.
              </p>
            </div>
          </div>
          <Link href="/dashboard/organization/organizer-requests" className="org-alert-btn">
            Review requests
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      ) : null}

      <div className="org-hero ud-enter ud-enter-2">
        <div className="org-hero-glow" aria-hidden />
        <div className="org-hero-inner">
          <div className="org-hero-main">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="org-hero-logo" />
            ) : (
              <div className="org-hero-logo-fallback">
                <Building2 className="h-7 w-7" aria-hidden />
              </div>
            )}
            <div className="min-w-0">
              <p className="org-hero-kicker">
                {greeting}
                {firstName ? ` · ${firstName}` : ''}
              </p>
              <h1 className="org-hero-title">{data.organization.name}</h1>
              <p className="org-hero-desc">
                {isNewOrg
                  ? 'Welcome! Set up your profile, invite organizers, and publish your first event to get started.'
                  : 'Monitor events, team activity, registrations, and revenue from one place.'}
              </p>
              {(orgType || data.organization.domain) && (
                <div className="org-hero-badges">
                  {orgType ? <span className="org-hero-badge">{orgType}</span> : null}
                  {data.organization.domain ? (
                    <span className="org-hero-badge">{data.organization.domain}</span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <div className="org-hero-actions">
            <Link href={createEventHref} className="ud-btn-hero">
              <Plus className="h-4 w-4" aria-hidden />
              Create event
            </Link>
            <Link href="/dashboard/organization/analytics" className="ud-btn-hero-ghost">
              <BarChart3 className="h-4 w-4" aria-hidden />
              Analytics
            </Link>
          </div>
        </div>
      </div>

      <div className="org-stat-grid ud-enter ud-enter-3">
        {stats.map((stat) => (
          <UserStatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            href={stat.href}
            hint={stat.hint}
            accent={stat.accent}
          />
        ))}
      </div>

      <div className="space-y-6 ud-enter ud-enter-4">
        <section className="org-panel">
          <div className="org-panel-head">
            <div>
              <h2 className="org-panel-title">Recent events</h2>
              <p className="org-panel-subtitle">Latest activity across your organization</p>
            </div>
            {recentEvents.length > 0 ? (
              <Link href="/dashboard/organization/events" className="org-panel-link">
                View all <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            ) : null}
          </div>

          {eventsLoading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
            </div>
          ) : recentEvents.length === 0 ? (
            <div className="org-empty">
              <div className="org-empty-icon">
                <CalendarDays className="h-7 w-7" aria-hidden />
              </div>
              <h3 className="text-base font-semibold text-[var(--color-text)]">No events yet</h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-[var(--color-text-muted)]">
                Create your first event to start accepting registrations and tracking performance here.
              </p>
              <Link href={createEventHref} className="ud-btn-primary mt-5">
                <Plus className="h-4 w-4" aria-hidden />
                Create your first event
              </Link>
            </div>
          ) : (
            <div className="org-events-grid">
              {recentEvents.map((event) => {
                const statusKey = (event.lifecycle_status || event.status || 'draft').toLowerCase();
                const badgeClass = EVENT_STATUS_CLASS[statusKey] ?? 'org-status-badge--draft';

                return (
                  <Link
                    key={event.id}
                    href={`/dashboard/events/${event.id}`}
                    className="org-event-card"
                  >
                    <div className="org-event-card-accent" aria-hidden />
                    <div className="org-event-card-body">
                      <div className="org-event-card-top">
                        <div className="org-event-card-icon">
                          <Calendar className="h-4 w-4" aria-hidden />
                        </div>
                        <span className={`org-status-badge ${badgeClass}`}>{eventStatusLabel(event)}</span>
                      </div>
                      <p className="org-event-card-title line-clamp-2">{event.title}</p>
                      <div className="org-event-card-stats">
                        <div className="org-event-card-stat-box">
                          <span className="org-event-card-stat-label">Date</span>
                          <span className="org-event-card-stat-value">{formatEventDate(event.start_date)}</span>
                        </div>
                        <div className="org-event-card-stat-box">
                          <span className="org-event-card-stat-label">Regs</span>
                          <span className="org-event-card-stat-value">
                            {typeof event.registrations_count === 'number' ? event.registrations_count : '—'}
                          </span>
                        </div>
                        <div className="org-event-card-stat-box">
                          <span className="org-event-card-stat-label">Status</span>
                          <span className="org-event-card-stat-value org-event-card-stat-value--cap">
                            {eventStatusLabel(event)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="org-event-card-foot">
                      <span className="org-event-card-open">
                        Open event <span aria-hidden>→</span>
                      </span>
                    </div>
                  </Link>
                );
              })}
              {visibleFillers.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.label} href={item.href} className="org-fill-card">
                    <div className="org-fill-card-icon">
                      <Icon className="h-4 w-4" aria-hidden />
                    </div>
                    <span className="org-fill-card-label">{item.label}</span>
                    <span className="org-fill-card-desc">{item.description}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="org-panel">
            <div className="org-panel-head">
              <div>
                <h2 className="org-panel-title">Setup progress</h2>
                <p className="org-panel-subtitle">Track how your organization is growing</p>
              </div>
            </div>
            <div className="org-progress-list">
              {setupMetrics.map((metric) => {
                const pct = progressPercent(metric.value, metric.target);
                const displayValue = String(metric.value);

                return (
                  <Link key={metric.label} href={metric.href} className="org-progress-item">
                    <div className="org-progress-row">
                      <span className="org-progress-label">{metric.label}</span>
                      <span className="org-progress-value">
                        {displayValue} / {metric.target}
                      </span>
                    </div>
                    <div className="org-progress-track">
                      <div className="org-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="org-panel">
            <div className="org-panel-head">
              <div>
                <h2 className="org-panel-title">At a glance</h2>
                <p className="org-panel-subtitle">Key numbers right now</p>
              </div>
            </div>
            <div className="org-snapshot">
              <Link href="/dashboard/organization/events" className="org-snapshot-item">
                <span className="org-snapshot-value">{data.active_events ?? 0}</span>
                <span className="org-snapshot-label">Active events</span>
              </Link>
              <Link href="/dashboard/organization/organizers" className="org-snapshot-item">
                <span className="org-snapshot-value">{data.total_organizers}</span>
                <span className="org-snapshot-label">Organizers</span>
              </Link>
              <Link href="/dashboard/organization/analytics" className="org-snapshot-item">
                <span className="org-snapshot-value">{registrations}</span>
                <span className="org-snapshot-label">Registrations</span>
              </Link>
              <Link href="/dashboard/organization/organizer-requests" className="org-snapshot-item">
                <span className="org-snapshot-value">{pendingApprovals}</span>
                <span className="org-snapshot-label">Pending</span>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
